import { useMemo, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Flex, Text } from "@radix-ui/themes";
import { mixTrackEvent, InteractionInformationEvents } from "@/assets/mixpanel";
import { isYotpo } from "@/api/user";
import {
  QaScore,
  ReasonForNegativeScore,
} from "@/v2/entities/qa-score/QaScore.model";
import { QaScoreResult } from "./QaScoreResult";
import { useMutation } from "react-query";
import {
  UpdateTicketScoreRequest,
  useSamplingApi,
} from "@/v2/entities/sampling/Sampling.api";

export function formatQuestionTitle(title: string): string {
  return title
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/-/g, " ")
    .toLowerCase()
    .replace(/^\w/, (char) => char.toUpperCase());
}

type TicketScore = QaScore & {
  onUpdateTicketScore: () => Promise<unknown>;
  viewOnly?: boolean;
};

export function TicketScore(props: TicketScore) {
  const { id, title, content, viewOnly } = props;
  const { user } = useAuth0();
  const samplingApi = useSamplingApi();
  const { mutateAsync } = useMutation({
    mutationFn: samplingApi.updateTicketScore,
  });

  const formattedTitle = useMemo(() => formatQuestionTitle(title), [title]);

  const isYotpoUser = useMemo(() => isYotpo(user), [user]);

  const updateTicketScore = useCallback(
    async (newScore: {
      adjustedScore?: number;
      comment?: string;
      reasonsForNegativeScore?: ReasonForNegativeScore[];
    }) => {
      const newTicketScore: UpdateTicketScoreRequest = {
        scoreId: id,
        adjustedScore: newScore.adjustedScore ?? props.adjustedScore,
        comment: newScore.comment ?? props.comment,
        reasonsForNegativeScore:
          newScore.reasonsForNegativeScore ?? props.reasonsForNegativeScore,
      };

      if (
        newScore.adjustedScore !== undefined &&
        props.adjustedScore !== newScore.adjustedScore
      ) {
        mixTrackEvent({
          event: InteractionInformationEvents.ADJUSTED_TICKET_AI_SCORE,
          properties: {
            ticketId: content,
            type: props.type,
            oldScore: props.adjustedScore ?? props.autoScore,
            newScore: newScore.adjustedScore,
          },
        });
      }

      await mutateAsync(newTicketScore);
      await props.onUpdateTicketScore();
    },
    [
      mutateAsync,
      props.onUpdateTicketScore,
      props.adjustedScore,
      props.comment,
      id,
      props.reasonsForNegativeScore,
    ]
  );

  return (
    <Flex key={id} direction="column" gap="3">
      {isYotpoUser ? (
        <Flex direction="column">
          <Text size="2" color="gray" weight="medium" highContrast>
            {content}
          </Text>
        </Flex>
      ) : (
        <Flex direction="column">
          <Text size="2" color="gray" weight="medium" highContrast>
            {formattedTitle}
          </Text>
          <Text size="2" color="gray">
            {content}
          </Text>
        </Flex>
      )}

      <QaScoreResult
        {...props}
        viewOnly={viewOnly}
        updateQaScore={updateTicketScore}
        showReasons={isYotpoUser && props.reasonForNegativeScore !== undefined}
        showComment={isYotpoUser && props.comment !== undefined}
      />
    </Flex>
  );
}
